// Colour-blind safe telegraph SHAPES (comfort & accessibility, owner-approved 2026-11-02). Render-only: reads Sim enemy
// modes / hostile projectiles and draws pattern cues so an attack's meaning never rests on red-vs-green:
//   - charge lanes (lancer, boss): a row of marching chevrons pointing down the lane
//   - slam circles (stomper) and the boss ring burst: a dashed ring at the hit radius + a hatched disc filling as the
//     wind-up runs out
//   - hostile shots: a ring around every enemy projectile (player shots never get one)
//   - every foe winding up: a "!" badge over its head
//   - map hazards: a crush zone in warning gets a dashed outline, a hatched fill growing to the hit and a "!"; burning
//     ground is hatched (slow / slip zones are not damaging: palette only)
// Drawn only while a colour-blind mode is on (CB.cues); colours come from the CB palette (src/render/a11y.ts).
import * as THREE from 'three/webgpu';
import type { Sim } from '../core/sim';
import { HazardField, HZ } from '../core/hazards';
import { CB, TelegraphKey } from './a11y';
import { AtlasQuads } from './pickups';

const _m = new THREE.Matrix4(), _p = new THREE.Vector3(), _s = new THREE.Vector3(), _q = new THREE.Quaternion(), _q2 = new THREE.Quaternion(), _c = new THREE.Color();
const _Y = new THREE.Vector3(0, 1, 0), _Z = new THREE.Vector3(0, 0, 1);
const TAU = Math.PI * 2;
/** 4×2 atlas: 0 chevron (points +x) · 1 dash (along x) · 2 ring · 3 "!" badge · 4 hatched disc */
const CELL = { chevron: 0, dash: 1, ring: 2, bang: 3, hatch: 4 } as const;

function atlas(): THREE.Texture {
  const S = 256, c = document.createElement('canvas'); c.width = S * 4; c.height = S * 2; const g = c.getContext('2d')!;
  g.lineCap = 'round'; g.lineJoin = 'round';
  const at = (cell: number, fn: () => void) => { g.save(); g.translate((cell % 4) * S + S / 2, Math.floor(cell / 4) * S + S / 2); fn(); g.restore(); };
  const INK = 'rgba(16,4,24,0.92)';
  /** dark ink underlay, then a white stroke the instance colour tints (ink stays dark, so it reads on any ground) */
  const inked = (path: () => void, w: number) => {
    g.strokeStyle = INK; g.lineWidth = w + 22; g.beginPath(); path(); g.stroke();
    g.strokeStyle = '#ffffff'; g.lineWidth = w; g.beginPath(); path(); g.stroke();
  };
  const R = S * 0.4;
  at(CELL.chevron, () => inked(() => { g.moveTo(-R * 0.55, -R * 0.8); g.lineTo(R * 0.5, 0); g.lineTo(-R * 0.55, R * 0.8); }, 34));
  at(CELL.dash, () => inked(() => { g.moveTo(-R * 0.85, 0); g.lineTo(R * 0.85, 0); }, 30));
  at(CELL.ring, () => inked(() => g.arc(0, 0, R, 0, TAU), 18));
  at(CELL.bang, () => {
    g.fillStyle = INK; g.beginPath(); g.arc(0, 0, R * 1.08, 0, TAU); g.fill();
    g.fillStyle = '#ffffff'; g.beginPath(); g.arc(0, 0, R * 0.9, 0, TAU); g.fill();
    g.fillStyle = INK; g.beginPath(); g.moveTo(-R * 0.16, -R * 0.62); g.lineTo(R * 0.16, -R * 0.62); g.lineTo(R * 0.09, R * 0.18); g.lineTo(-R * 0.09, R * 0.18); g.closePath(); g.fill();
    g.beginPath(); g.arc(0, R * 0.46, R * 0.13, 0, TAU); g.fill();
  });
  at(CELL.hatch, () => { // diagonal hatching inside a circle: "this area is about to be hit"
    g.save(); g.beginPath(); g.arc(0, 0, R * 1.2, 0, TAU); g.clip();
    for (let k = -8; k <= 8; k++) { const o = k * R * 0.3; g.strokeStyle = INK; g.lineWidth = 20; g.beginPath(); g.moveTo(o - R * 1.3, -R * 1.3); g.lineTo(o + R * 1.3, R * 1.3); g.stroke(); g.strokeStyle = 'rgba(255,255,255,0.85)'; g.lineWidth = 9; g.beginPath(); g.moveTo(o - R * 1.3, -R * 1.3); g.lineTo(o + R * 1.3, R * 1.3); g.stroke(); }
    g.restore();
  });
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4;
  return t;
}

export class Telegraphs {
  private ground: AtlasQuads;
  private bill: AtlasQuads;
  private flat = new THREE.Quaternion().setFromEuler(new THREE.Euler(-Math.PI / 2, 0, 0));
  /** QA counters (?a11ycheck) */
  stats = { lanes: 0, rings: 0, shots: 0, bangs: 0, hazards: 0 };

  constructor(scene: THREE.Scene, private groundY: (x: number, z: number) => number) {
    const tex = atlas();
    this.ground = new AtlasQuads(new THREE.PlaneGeometry(1, 1), tex, 900, false, 8);
    this.bill = new AtlasQuads(new THREE.PlaneGeometry(1, 1), tex, 400, false, 9);
    (this.ground.mesh.material as THREE.Material).depthTest = true;
    (this.bill.mesh.material as THREE.Material).depthTest = false; // badges and shot rings stay on top of bodies
    scene.add(this.ground.mesh, this.bill.mesh);
  }

  private tint(k: TelegraphKey, gain = 1.25): [number, number, number] {
    _c.setHex(CB.tc(k)); return [_c.r * gain, _c.g * gain, _c.b * gain];
  }
  /** flat ground quad at (x, z), its +x along (dx, dz) */
  private flatQuad(x: number, z: number, dx: number, dz: number, sx: number, sy: number, cell: number, a: number, col: [number, number, number]): void {
    _q.setFromAxisAngle(_Y, Math.atan2(-dz, dx)).multiply(this.flat);
    _p.set(x, this.groundY(x, z) + 0.07, z); _s.set(sx, sy, 1);
    this.ground.push(_m.compose(_p, _q, _s), cell, a, col[0], col[1], col[2]);
  }
  private billQuad(x: number, y: number, z: number, size: number, cell: number, a: number, col: [number, number, number], camQ: THREE.Quaternion, roll = 0): void {
    _q2.setFromAxisAngle(_Z, roll); _q.copy(camQ).multiply(_q2);
    _p.set(x, y, z); _s.set(size, size, 1);
    this.bill.push(_m.compose(_p, _q, _s), cell, a, col[0], col[1], col[2]);
  }
  /** marching chevrons from the foe down its charge lane */
  private lane(x: number, z: number, dx: number, dz: number, len: number, w: number, prog: number, t: number, k: TelegraphKey): void {
    const col = this.tint(k), step = w * 1.15, off = (t * 5) % step;
    for (let d = w * 0.9 + off; d < len; d += step) {
      const fade = Math.min(1, (len - d) / step) * (0.8 + 0.2 * prog);
      this.flatQuad(x + dx * d, z + dz * d, dx, dz, w, w, CELL.chevron, fade, col);
    }
    this.stats.lanes++;
  }
  /** dashed ring at the hit radius + hatched disc that grows as the wind-up runs out */
  private ring(x: number, z: number, r: number, prog: number, t: number, k: TelegraphKey): void {
    const col = this.tint(k), n = Math.max(10, Math.round((TAU * r) / 1.15)), a0 = t * 0.5, len = (TAU * r / n) * 0.62;
    for (let i = 0; i < n; i++) {
      const a = a0 + (i / n) * TAU, px = x + Math.cos(a) * r, pz = z + Math.sin(a) * r;
      this.flatQuad(px, pz, -Math.sin(a), Math.cos(a), len, 0.7, CELL.dash, 1, col);
    }
    const d = 2 * r * Math.max(0.08, Math.min(1, prog));
    this.flatQuad(x, z, 1, 0, d, d, CELL.hatch, 0.6, col);
    this.stats.rings++;
  }

  /** dashed outline of a rotated rectangle (half extents hx along (cos rot, sin rot), hz across) */
  private rectDashes(x: number, z: number, hx: number, hz: number, rot: number, col: [number, number, number]): void {
    const ax = Math.cos(rot), az = Math.sin(rot), bx = -az, bz = ax;
    for (const [ex, ez, len, ox, oz] of [[ax, az, hx, bx * hz, bz * hz], [ax, az, hx, -bx * hz, -bz * hz], [bx, bz, hz, ax * hx, az * hx], [bx, bz, hz, -ax * hx, -az * hx]]) {
      const n = Math.max(2, Math.round((2 * len) / 1.15));
      for (let i = 0; i < n; i++) { const u = ((i + 0.5) / n) * 2 - 1; this.flatQuad(x + ox + ex * len * u, z + oz + ez * len * u, ex, ez, (2 * len / n) * 0.62, 0.7, CELL.dash, 1, col); }
    }
  }
  private hazards(sim: Sim, t: number, camQ: THREE.Quaternion, bangCol: [number, number, number]): void {
    for (const zn of sim.hazards.zones) {
      if (zn.global || (zn.phase !== HZ.warn && zn.phase !== HZ.active)) continue;
      const rect = !!zn.l, hx = rect ? zn.l! : zn.r, hz = rect ? zn.w ?? 1 : zn.r, rot = zn.rot ?? 0;
      if (zn.kind === 'crush' && zn.phase === HZ.warn) {
        const col = this.tint('hazardCrush'), k = Math.max(0.08, Math.min(1, HazardField.warnK(zn)));
        if (rect) this.rectDashes(zn.x, zn.z, hx, hz, rot, col);
        else { const n = Math.max(10, Math.round((TAU * hx) / 1.15)), len = (TAU * hx / n) * 0.62; for (let i = 0; i < n; i++) { const a = t * 0.5 + (i / n) * TAU; this.flatQuad(zn.x + Math.cos(a) * hx, zn.z + Math.sin(a) * hx, -Math.sin(a), Math.cos(a), len, 0.7, CELL.dash, 1, col); } }
        this.flatQuad(zn.x, zn.z, Math.cos(rot), Math.sin(rot), 2 * hx * k, 2 * hz * k, CELL.hatch, 0.6, col);
        this.billQuad(zn.x, this.groundY(zn.x, zn.z) + 1.6, zn.z, 1.4, CELL.bang, 1, bangCol, camQ);
        this.stats.hazards++; this.stats.bangs++;
      } else if (zn.kind === 'burn') {
        this.flatQuad(zn.x, zn.z, Math.cos(rot), Math.sin(rot), 2 * hx, 2 * hz, CELL.hatch, zn.phase === HZ.active ? 0.45 : 0.3, this.tint('hazardBurn'));
        this.stats.hazards++;
      }
    }
  }

  update(sim: Sim, t: number, cam: THREE.Camera): void {
    this.ground.n = 0; this.bill.n = 0; this.stats.lanes = this.stats.rings = this.stats.shots = this.stats.bangs = this.stats.hazards = 0;
    if (CB.cues) {
      const camQ = cam.quaternion, bangCol = this.tint('chargeCue', 1);
      for (const e of sim.enemies) {
        if (!e.alive) continue;
        const kind = e.def.kind, rr = e.def.radius * e.scale;
        let tele = false;
        if (kind === 'lancer' && e.mode === 1) { this.lane(e.x, e.z, e.dirX, e.dirZ, 10, 1.4, 1 - Math.max(0, e.modeT) / 0.75, t, 'chargeLane'); tele = true; }
        else if (kind === 'stomper' && e.mode === 1) { this.ring(e.x, e.z, 4.5 * e.scale, 1 - Math.max(0, e.modeT) / 0.9, t, 'slamCircle'); tele = true; }
        else if (kind === 'boss' && e.mode === 1) { this.lane(e.x, e.z, e.dirX, e.dirZ, 22, 2.1, 1 - Math.max(0, e.modeT) / 0.9, t, 'bossCharge'); tele = true; }
        else if (kind === 'boss' && e.mode === 2) { this.ring(e.x, e.z, rr + 4.5, 1 - Math.max(0, e.modeT) / 0.6, t, 'bossRing'); tele = true; }
        else if (kind === 'boss' && e.mode === 3) tele = true; // summon: the badge is the cue
        else if (kind === 'boss' && e.mode === 6) { this.ring(e.x, e.z, sim.bossFormInfo(e)?.slamR || 8, 1 - Math.max(0, e.modeT) * sim.pace, t, 'slamCircle'); tele = true; } // BOSS FORMS: braced slam
        else if (kind === 'boss' && (e.mode === 5 || e.mode === 7 || e.mode === 8)) tele = true; // BOSS FORMS: fan / rain / spiral
        else if (e.form > 0 && sim.bossOverlayTele(e) > 0) tele = true; // BOSS FORMS: a guardian's overlay attack
        else if (e.def.map && sim.foes.windK(e) > 0) tele = true; // MAP FOES: winding up (its mark is drawn below)
        if (tele) {
          const top = this.groundY(e.x, e.z) + Math.max(e.y, 0) * 0.5 + rr * (kind === 'boss' ? 3.4 : 2.6) + 1.1;
          this.billQuad(e.x, top + Math.sin(t * 6) * 0.08, e.z, kind === 'boss' ? 2.2 : 1.25, CELL.bang, 1, bangCol, camQ);
          this.stats.bangs++;
        }
      }
      this.hazards(sim, t, camQ, bangCol);
      for (const m of sim.bossMarks) this.ring(m.x, m.z, m.r, m.t / m.warn, t, 'hazardCrush'); // BOSS FORMS: impact marks
      // MAP FOES: attack marks: lanes as marching chevrons from the foe, circles as a dashed ring + hatched fill
      for (const m of sim.foes.marks) {
        const k = Math.min(1, m.t / m.warn);
        if (m.l > 0) { const dx = Math.cos(m.rot), dz = Math.sin(m.rot); this.lane(m.x - dx * m.l, m.z - dz * m.l, dx, dz, 2 * m.l, Math.max(1.2, m.w * 1.4), k, t, m.hurt ? 'chargeLane' : 'hazardSlip'); }
        else this.ring(m.x, m.z, m.r, k, t, 'hazardCrush');
      }
      const shotCol = this.tint('hostileShot', 1.4);
      for (const pr of sim.projectiles) {
        if (!pr.alive || !pr.hostile) continue;
        this.billQuad(pr.x, pr.y, pr.z, 1.7, CELL.ring, 1, shotCol, camQ, t * 3);
        this.stats.shots++;
      }
    }
    this.ground.end(); this.bill.end();
  }

  clear(): void { this.ground.n = 0; this.bill.n = 0; this.ground.end(); this.bill.end(); }
}
